import { Themes } from '@newgenus/common';
import type { User } from '@newgenus/common';

export interface FirstTimeSetupIdentity {
    uid: string;
    email?: string;
    displayName?: string;
}

/**
 * Build the one-time "first-time user setup" payload — **set-if-absent**.
 *
 * This routine is meant to run exactly once, for a brand-new user. But a
 * cold-cache race at login can make it run again for an already-onboarded user
 * (Firestore latency-compensation hands back a partial doc as the first
 * emission). If it does, it must NOT reset fields the user owns — above all it
 * must never downgrade `isDeveloper`. So every user-owned/customizable field is
 * included ONLY when it's absent from the existing doc; identity + `setupDone`
 * are always written (idempotent).
 *
 * Pure function (no AngularFire) so it can be unit-tested directly.
 */
/** The server read that confirms a user really is new, or `{ ok: false }` when it could not be made. */
export type ServerUserRead = { ok: false } | { ok: true; exists: boolean; data?: Partial<User> };

export type FirstTimeSetupDecision =
    | { action: 'retry' }
    | { action: 'skip' }
    | { action: 'run'; existing: Partial<User> | null };

/**
 * Whether the one-time setup may run, decided ONLY from a successful server read (OE-215).
 * A failed read is not "no doc": treating it so wrote every default over an onboarded user
 * and demoted a developer. It retries on a later emission instead.
 */
export function decideFirstTimeSetup(read: ServerUserRead): FirstTimeSetupDecision {
    if (!read.ok) return { action: 'retry' };
    if (!read.exists) return { action: 'run', existing: null };
    if (read.data?.setupDone) return { action: 'skip' };
    return { action: 'run', existing: read.data ?? {} };
}

export function buildFirstTimeSetupData(
    user: FirstTimeSetupIdentity,
    existing: Partial<User> | null | undefined,
    photoURL?: string
): Record<string, unknown> {
    const e = (existing ?? {}) as Record<string, unknown>;

    // Always written — identity (refreshed each login) + the setup marker.
    const data: Record<string, unknown> = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        setupDone: true,
    };

    // Owned-once fields: set only when absent so a re-run can never clobber
    // the user's value. `=== undefined` (not falsy) so an explicit `false`
    // is preserved too.
    // `isDeveloper` is deliberately NEVER written here (OE-215): absent already reads as "not a
    // developer" everywhere, and a login that raced a partial read wrote `false` over real developers.
    if (e['theme'] === undefined) data['theme'] = Themes['light-theme'];
    if (e['preferences'] === undefined) {
        const preferences: Record<string, unknown> = {
            sidebarStatus: false,
            particleLabelsOrder: ['SYSTEM', 'EMAIL', 'CHECKLIST', 'ORDER', 'DISPATCH'],
            dateSorting: 'desc',
            typeOfSort: 'date_type',
            signature: { type: 'OneForAll' },
        };
        if (photoURL) preferences['photoURL'] = photoURL;
        data['preferences'] = preferences;
    }
    if (e['serverExcludedLabels'] === undefined) data['serverExcludedLabels'] = [];
    if (e['excludedLabels'] === undefined) {
        data['excludedLabels'] = ['CATEGORY_UPDATES', 'CATEGORY_PROMOTIONS', 'CATEGORY_SOCIAL', 'CATEGORY_FORUMS'];
    }
    if (e['includedLabels'] === undefined) data['includedLabels'] = ['SAMPLE'];

    return data;
}
